/**
 * CandidateMessages — /candidate/messages
 *
 * Candidate-facing DaddyTalk chat history. Mirrors DaddyTalkInbox but
 * scoped to `as_role=initiator` and hides recruiter-only actions
 * (Accept / Decline / Fast-track). Uncle D AI messages render with
 * their distinct branding so candidates immediately know when they're
 * chatting with a human vs the AI concierge.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PushOptInBanner from "@/components/PushOptInBanner";
import {
    PaperPlaneTilt,
    Circle,
    Clock,
    UserCircle,
    ArrowLeft,
} from "@phosphor-icons/react";
import { VoiceRecorder, VoicePlayer } from "@/components/chat/VoiceRecorder";
import {
    useSwipeToReply,
    ReplyQuote,
    ReplyPreviewBar,
    SwipeReplyIndicator,
} from "@/components/chat/SwipeToReply";
import ForwardMessageModal from "@/components/chat/ForwardMessageModal";
import ChatAvatar from "@/components/chat/ChatAvatar";
import { useChatDensity, ChatDensityToggle } from "@/lib/useChatDensity";
import {
    ReactionBar,
    ReactionPicker,
    QuickReactionsHoverStrip,
    useLongPress,
} from "@/components/chat/ReactionPicker";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const REFRESH_INTERVAL_MS = 15000;  // V1.9.17 — tightened from 60s so latency stays low when SSE drops

function formatWhen(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return d.toLocaleDateString();
    } catch { return ""; }
}

export default function CandidateMessages() {
    const { user } = useAuth();
    const [sp] = useSearchParams();
    const [threads, setThreads] = useState([]);
    const [selectedId, setSelectedId] = useState(sp.get("thread") || null);
    const [convo, setConvo] = useState(null);
    const [density, setDensity] = useChatDensity();
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [replyTo, setReplyTo] = useState(null);  // V1.9.27
    const [forwardMsg, setForwardMsg] = useState(null);  // V1.9.29
    const pollRef = useRef();

    const loadThreads = useCallback(async () => {
        try {
            const res = await api.get("/daddytalk/threads?as_role=initiator");
            setThreads(res?.data?.items || []);
        } catch (_) { /* silent */ } finally { setLoading(false); }
    }, []);

    const loadConvo = useCallback(async (id) => {
        if (!id) { setConvo(null); return; }
        try {
            const res = await api.get(`/daddytalk/threads/${id}`);
            setConvo(res?.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load conversation");
        }
    }, []);

    useEffect(() => {
        loadThreads();
        pollRef.current = setInterval(() => {
            if (document.visibilityState === "visible") {
                loadThreads();
                if (selectedId) loadConvo(selectedId);
            }
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(pollRef.current);
    }, [loadThreads, loadConvo, selectedId]);

    useEffect(() => { loadConvo(selectedId); }, [selectedId, loadConvo]);

    // Real-time — reuses M4 SSE bridge in features.jsx
    useEffect(() => {
        const onChange = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id === selectedId) loadConvo(selectedId);
            loadThreads();
        };
        // V1.6.3 — When a recruiter comes back online, if the candidate
        // has a thread with them (pending or accepted, not closed),
        // surface a friendly toast pointing to that thread. Debounced
        // via a Set so a burst of tab-focus heartbeats doesn't spam.
        const nudgedRef = new Set();
        const onRecruiterOnline = (ev) => {
            const { recruiter_id, recruiter_name } = ev?.detail || {};
            if (!recruiter_id || nudgedRef.has(recruiter_id)) return;
            const match = threads.find(
                (t) => t.counterparty?.id === recruiter_id && t.status !== "closed",
            );
            if (!match) return;
            nudgedRef.add(recruiter_id);
            toast.success(
                `${recruiter_name || "Your recruiter"} just came online — reply live!`,
                {
                    action: {
                        label: "Open chat",
                        onClick: () => setSelectedId(match.id),
                    },
                    duration: 8000,
                },
            );
            // Clear the debounce after 5 min so a genuine re-appear
            // later still nudges.
            setTimeout(() => nudgedRef.delete(recruiter_id), 5 * 60 * 1000);
        };
        window.addEventListener("daddytalk:thread-created", loadThreads);
        window.addEventListener("daddytalk:message-new", onChange);
        window.addEventListener("daddytalk:recruiter-online", onRecruiterOnline);
        const onReacted = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id !== selectedId) return;
            setConvo((prev) => prev ? ({
                ...prev,
                messages: prev.messages.map((m) =>
                    m.id === detail.message_id ? { ...m, reactions: detail.reactions } : m,
                ),
            }) : prev);
        };
        window.addEventListener("daddytalk:message-reacted", onReacted);
        const onUpdated = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id !== selectedId) return;
            setConvo((prev) => prev ? ({
                ...prev,
                messages: prev.messages.map((m) => {
                    if (m.id !== detail.message_id) return m;
                    const attPatch = detail.attachment || {};
                    return { ...m, attachment: { ...(m.attachment || {}), ...attPatch } };
                }),
            }) : prev);
        };
        window.addEventListener("daddytalk:message-updated", onUpdated);
        return () => {
            window.removeEventListener("daddytalk:thread-created", loadThreads);
            window.removeEventListener("daddytalk:message-new", onChange);
            window.removeEventListener("daddytalk:recruiter-online", onRecruiterOnline);
            window.removeEventListener("daddytalk:message-reacted", onReacted);
            window.removeEventListener("daddytalk:message-updated", onUpdated);
        };
    }, [loadThreads, loadConvo, selectedId, threads]);

    const unreadCount = useMemo(
        () => threads.filter((t) => t.unread && t.status !== "closed").length, [threads],
    );

    async function sendReply() {
        const body = reply.trim();
        if (!body || !selectedId || sending) return;
        setSending(true);
        try {
            await api.post(`/daddytalk/threads/${selectedId}/messages`, {
                body,
                ...(replyTo ? { reply_to_message_id: replyTo.id } : {}),
            });
            setReply("");
            setReplyTo(null);
            await loadConvo(selectedId);
            await loadThreads();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send reply");
        } finally { setSending(false); }
    }

    async function sendVoice(blob, durationMs) {
        if (!selectedId || sending) return;
        setSending(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, `voice.${(blob.type.split("/")[1] || "webm").split(";")[0]}`);
            fd.append("duration_ms", String(durationMs));
            if (replyTo) fd.append("reply_to_message_id", replyTo.id);
            await api.post(`/daddytalk/threads/${selectedId}/messages/voice`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setReplyTo(null);
            await loadConvo(selectedId);
            await loadThreads();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send voice note");
        } finally { setSending(false); }
    }

    async function handleReact(messageId, emoji) {
        setConvo((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                messages: prev.messages.map((m) => {
                    if (m.id !== messageId) return m;
                    const rx = { ...(m.reactions || {}) };
                    const list = new Set(rx[emoji] || []);
                    if (list.has(user?.id)) list.delete(user?.id); else list.add(user?.id);
                    if (list.size === 0) delete rx[emoji]; else rx[emoji] = [...list];
                    return { ...m, reactions: rx };
                }),
            };
        });
        try {
            await api.post(`/daddytalk/messages/${messageId}/react`, { emoji });
        } catch (err) {
            toast.error("Couldn't save reaction. Please retry.");
            loadConvo(selectedId);
        }
    }

    async function handleStar(messageId) {
        setConvo((prev) => {
            if (!prev) return prev;
            return { ...prev, messages: prev.messages.map((m) => {
                if (m.id !== messageId) return m;
                const arr = new Set(m.starred_by_user_ids || []);
                if (arr.has(user?.id)) arr.delete(user?.id); else arr.add(user?.id);
                return { ...m, starred_by_user_ids: [...arr] };
            }) };
        });
        try { await api.post(`/daddytalk/messages/${messageId}/star`); }
        catch { toast.error("Couldn't toggle star."); loadConvo(selectedId); }
    }

    return (
        <div className="flex-1 min-h-0 flex flex-col bg-cream" data-testid="candidate-messages-root">
            {/* V1.7.0 Sprint 1 — contextual push opt-in. Renders only if
                the user hasn't granted permission yet + isn't dismissed. */}
            <PushOptInBanner user={user} context="chat" />
            <header className="bg-white border-b border-ink/10 px-4 sm:px-6 py-4 flex items-center gap-3">
                <img
                    src="/mascot/daddytalk/avatar_hello.png"
                    alt=""
                    className="w-10 h-10 rounded-full border-2 border-cream"
                />
                <div>
                    <h1 className="font-heading text-lg font-black">My DaddyTalk chats</h1>
                    <p className="text-xs text-ink/50">
                        {unreadCount > 0
                            ? `${unreadCount} recruiter${unreadCount === 1 ? " has" : "s have"} replied`
                            : "All caught up — recruiters usually reply within 2 hours."}
                    </p>
                </div>
            </header>

            <div className="flex-1 min-h-0 max-w-5xl w-full mx-auto grid grid-cols-1 md:grid-cols-[minmax(260px,320px)_1fr] gap-0">
                {/* Thread list */}
                <aside
                    className={`border-r border-ink/10 bg-white overflow-y-auto ${selectedId ? "hidden md:block" : "block"}`}
                    data-testid="candidate-messages-list"
                    data-density={density}
                >
                    {loading ? (
                        <div className="p-6 text-xs text-ink/50">Loading…</div>
                    ) : threads.length === 0 ? (
                        <div className="p-6 text-center">
                            <img src="/mascot/daddytalk/avatar_thinking.png" alt=""
                                 className="w-20 h-20 rounded-full mx-auto mb-3" />
                            <p className="font-heading font-bold text-sm mb-1">No chats yet</p>
                            <p className="text-xs text-ink/50 mb-4">
                                Browse recruiters and start your first conversation on DaddyTalk.
                            </p>
                            <a href="/daddytalk"
                               className="inline-flex items-center gap-2 px-4 py-2 bg-cobalt text-white text-[11px] font-black uppercase tracking-[0.2em]">
                                Find a recruiter
                            </a>
                        </div>
                    ) : threads.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setSelectedId(t.id)}
                            data-testid={`candidate-thread-${t.id}`}
                            data-role="thread-row"
                            className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${selectedId === t.id ? "bg-black/5" : "hover:bg-black/5"}`}
                        >
                            <div className="thread-avatar shrink-0">
                                <ChatAvatar
                                    name={t.counterparty?.name || "Recruiter"}
                                    src={t.counterparty?.avatar_url}
                                    size="md"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2">
                                    <p className={`truncate ${t.unread ? "font-black" : "font-bold"} text-[15px] text-ink`}>
                                        {t.counterparty?.name || "Recruiter"}
                                    </p>
                                    <span className={`text-[11px] whitespace-nowrap shrink-0 ${t.unread ? "text-emerald-600 font-bold" : "text-ink/45"}`}>
                                        {formatWhen(t.last_message_at)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between gap-2 mt-0.5">
                                    <p className={`text-[13px] truncate ${t.unread ? "text-ink/80" : "text-ink/55"}`}>
                                        {t.last_message_from_me ? "You: " : ""}{t.last_message_preview || " "}
                                    </p>
                                    {t.unread && (
                                        <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-black inline-flex items-center justify-center shrink-0">•</span>
                                    )}
                                </div>
                                {t.status && t.status !== "active" && (
                                    <div className="mt-1"><StatusBadge status={t.status} /></div>
                                )}
                            </div>
                        </button>
                    ))}
                </aside>

                {/* Conversation */}
                <section
                    className={`flex flex-col min-h-0 bg-cream/30 ${selectedId ? "block" : "hidden md:flex"}`}
                    data-testid="candidate-messages-convo"
                >
                    {!selectedId ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                            <img src="/mascot/daddytalk/avatar_neutral.png" alt=""
                                 className="w-24 h-24 rounded-full mb-4 shadow-sm" />
                            <p className="font-heading font-bold text-base mb-1">Pick a chat to open it</p>
                            <p className="text-xs text-ink/50">Your DaddyTalk history lives here.</p>
                        </div>
                    ) : !convo ? (
                        <div className="flex-1 flex items-center justify-center text-xs text-ink/50">Loading…</div>
                    ) : (
                        <>
                            <div className="px-3 py-2.5 border-b border-ink/10 flex items-center gap-3 bg-[#f0f2f5]">
                                <button type="button" onClick={() => setSelectedId(null)}
                                        className="md:hidden text-ink/60 hover:text-ink" aria-label="Back">
                                    <ArrowLeft size={18} weight="bold" />
                                </button>
                                <ChatAvatar
                                    name={convo.counterparty?.name || "Recruiter"}
                                    src={convo.counterparty?.avatar_url}
                                    size="sm"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-[15px] truncate text-ink">{convo.counterparty?.name || "Recruiter"}</p>
                                    <StatusBadge status={convo.thread.status} />
                                </div>
                                <ChatDensityToggle density={density} setDensity={setDensity} testid="candidate-messages-density-toggle" />
                            </div>

                            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 chat-surface" data-density={density}>
                                {convo.messages.map((m) => {
                                    let replyQuoted = null;
                                    if (m.reply_to_message_id) {
                                        const src = convo.messages.find((x) => x.id === m.reply_to_message_id);
                                        if (src) {
                                            replyQuoted = {
                                                sender_name: src.sender_id === user?.id ? "You" : (convo.counterparty?.name || "Recruiter"),
                                                body: src.body,
                                                attachment_kind: src.attachment?.kind
                                                    || (src.attachment?.content_type?.startsWith("image/") && "image")
                                                    || (src.attachment ? "file" : null),
                                            };
                                        }
                                    }
                                    return (
                                        <MsgBubble
                                            key={m.id}
                                            m={m}
                                            isMine={m.sender_id === user?.id}
                                            myUserId={user?.id}
                                            onReact={handleReact}
                                            onStar={handleStar}
                                            onReply={(msg) => {
                                                setReplyTo({
                                                    id: msg.id,
                                                    sender_name: msg.sender_id === user?.id ? "You" : (convo.counterparty?.name || "Recruiter"),
                                                    body: msg.body,
                                                    attachment_kind: msg.attachment?.kind
                                                        || (msg.attachment?.content_type?.startsWith("image/") && "image")
                                                        || (msg.attachment ? "file" : null),
                                                });
                                            }}
                                            onForward={(msg) => setForwardMsg(msg)}
                                            replyQuoted={replyQuoted}
                                        />
                                    );
                                })}
                            </div>

                            {convo.thread.status !== "closed" && convo.thread.status !== "declined" && (
                                <div className="border-t border-ink/10 p-2.5 bg-[#f0f2f5] safe-bottom">
                                    {replyTo && (
                                        <ReplyPreviewBar
                                            quoted={replyTo}
                                            onCancel={() => setReplyTo(null)}
                                        />
                                    )}
                                    <div className="flex gap-2 items-center">
                                        <textarea
                                            value={reply}
                                            rows={1}
                                            onChange={(e) => {
                                                setReply(e.target.value);
                                                const el = e.currentTarget;
                                                el.style.height = "auto";
                                                el.style.height = Math.min(el.scrollHeight, 120) + "px";
                                            }}
                                            onKeyDown={(e) => {
                                                const isTouch = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
                                                if (e.key === "Enter" && !e.shiftKey && !isTouch) {
                                                    e.preventDefault();
                                                    sendReply();
                                                }
                                            }}
                                            placeholder="Message"
                                            data-testid="candidate-reply-input"
                                            className="flex-1 min-h-[40px] max-h-[120px] px-4 py-2 bg-white text-[14px] leading-[20px] focus:outline-none rounded-2xl shadow-sm placeholder:text-ink/40 resize-none overflow-y-auto"
                                        />
                                        {reply.trim() ? (
                                            <button
                                                type="button" onClick={sendReply}
                                                disabled={!reply.trim() || sending}
                                                data-testid="candidate-reply-send"
                                                className="btn-gradient inline-flex items-center justify-center gap-1.5 h-10 px-5 shrink-0 text-[11px] font-black uppercase tracking-wider disabled:opacity-40 rounded-full"
                                            >
                                                {sending ? "…" : "Send"}
                                                <PaperPlaneTilt size={11} weight="bold" />
                                            </button>
                                        ) : (
                                            <VoiceRecorder
                                                onSend={sendVoice}
                                                disabled={sending}
                                                onError={(m) => toast.error(m)}
                                                testId="candidate-voice-record-btn"
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>
            <ForwardMessageModal
                open={!!forwardMsg}
                onClose={() => setForwardMsg(null)}
                sourceMessage={forwardMsg}
                context="daddytalk"
            />
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        pending: { label: "Awaiting reply", cls: "bg-amber-100 text-amber-700" },
        accepted: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
        declined: { label: "Declined", cls: "bg-red-100 text-red-700" },
        closed: { label: "Closed", cls: "bg-ink/10 text-ink/50" },
    };
    const s = map[status] || map.pending;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${s.cls} mt-1`}>
            <Circle size={6} weight="fill" />{s.label}
        </span>
    );
}

function MsgBubble({ m, isMine, myUserId, onReact, onReply, onForward, onStar, replyQuoted }) {
    const isAI = m.sender_role === "ai" || m.ai_generated;
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const longPress = useLongPress(() => setPickerOpen(true));
    const isVoice = m.attachment?.kind === "voice";
    const audioSrc = isVoice
        ? `${API_BASE}/api/daddytalk/messages/${m.id}/attachment`
        : null;
    const isStarred = !!(myUserId && (m.starred_by_user_ids || []).includes(myUserId));
    const swipe = useSwipeToReply({
        onReply: () => onReply?.(m),
        onForward: () => onForward?.(m),
        isMine,
        disabled: (!onReply && !onForward) || isAI,
    });

    if (isAI) return (
        <div className="flex items-start gap-2">
            <img src="/mascot/daddytalk/avatar_thinking.png" alt=""
                 className="w-8 h-8 rounded-full border-2 border-white shrink-0" />
            <div className="max-w-[85%] px-4 py-2.5 text-sm bg-gradient-to-br from-cobalt/10 to-cobalt/5 border border-cobalt/30 rounded-t-2xl rounded-br-2xl">
                <p className="text-[10px] font-black uppercase tracking-wider text-cobalt mb-1">Uncle D · AI concierge</p>
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className="text-[9px] mt-1 text-ink/40">{formatWhen(m.created_at)}</p>
            </div>
        </div>
    );
    return (
        <div className={`flex ${isMine ? "justify-end" : "justify-start"} relative`}>
            <SwipeReplyIndicator progress={swipe.progress} isMine={isMine} side={swipe.swipeSide} />
            <div
                ref={swipe.wrapRef}
                className={`group flex flex-col ${isMine ? "items-end" : "items-start"} max-w-[85%] relative`}
                style={{
                    touchAction: "pan-y",
                }}
                {...swipe.handlers}
            >
                <QuickReactionsHoverStrip
                    onPick={(e) => onReact?.(m.id, e)}
                    onStar={onStar ? () => onStar(m.id) : undefined}
                    isStarred={isStarred}
                    isMine={isMine}
                    testIdPrefix={`candidate-quick-react-${m.id}`}
                />
                <div
                    {...longPress}
                    onDoubleClick={() => setPickerOpen(true)}
                    data-testid={`daddytalk-bubble-${m.id}`}
                    className={`relative px-2.5 py-1.5 text-[14px] leading-[19px] select-none min-w-0 max-w-full overflow-hidden ${
                        isMine
                            ? "bg-cobalt text-white rounded-2xl rounded-br-md"
                            : "bg-white border border-ink/10 text-ink rounded-2xl rounded-bl-md"
                    }`}
                    style={{ minWidth: "72px" }}
                >
                    {m.reply_to_message_id && replyQuoted && (
                        <ReplyQuote quoted={replyQuoted} isMine={isMine} />
                    )}
                    {isVoice ? (
                        <VoicePlayer
                            src={audioSrc}
                            durationMs={m.attachment.duration_ms}
                            transcript={m.attachment.transcript}
                            isMine={isMine}
                            testId={`daddytalk-voice-${m.id}`}
                        />
                    ) : (
                        <span className="whitespace-pre-wrap break-words">{m.body}</span>
                    )}
                    <span
                        className={`float-right ml-2 mt-1 text-[10px] leading-none whitespace-nowrap inline-flex items-center gap-1 pt-0.5 pl-1 select-none ${
                            isMine ? "text-white/75" : "text-ink/45"
                        }`}
                    >
                        {isStarred && <span className="text-amber-400">★</span>}
                        {formatWhen(m.created_at)}
                    </span>
                    <span className="clear-both block" />
                    <ReactionPicker
                        open={pickerOpen}
                        onDismiss={() => setPickerOpen(false)}
                        onPick={(e) => { setPickerOpen(false); onReact?.(m.id, e); }}
                        onStar={onStar ? () => { setPickerOpen(false); onStar(m.id); } : undefined}
                        isStarred={isStarred}
                        anchor="top"
                    />
                </div>
                <ReactionBar
                    reactions={m.reactions}
                    myUserId={myUserId}
                    onToggle={(e) => onReact?.(m.id, e)}
                    testIdPrefix={`daddytalk-reaction-${m.id}`}
                />
            </div>
        </div>
    );
}
